import * as preact from "preact";

import * as analytics from "analytics";
import * as haptics from "haptics";
import readlangClient from "readlangClient";
import * as sharePrompt from "sharePrompt";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import * as signupModal from "signupModal";
import type { BookData } from "stores/BookStore";
import BookStore, { displayTitle } from "stores/BookStore";
import RemoteUserWordsStore from "stores/RemoteUserWordsStore";
import stores from "stores/stores";
import Button from "ui/Button";

interface EndOfTextProps {
  model: BookStore;
  remoteUserWords: RemoteUserWordsStore;
  starterStory?: boolean;
  /** In the scroll footer under the text, which scrolls it into view */
  scrollMode?: boolean;
}

interface ContinueText {
  _id: string;
  title?: string;
}

interface EndOfTextState {
  vote: number;
  votedAlready: boolean;
  userWordsFetched: boolean;
  continueText?: ContinueText;
}

export class EndOfText extends preact.Component<
  EndOfTextProps,
  EndOfTextState
> {
  private endOfTextRef = preact.createRef<HTMLDivElement>();
  private pendingFit = 0;

  state: EndOfTextState = {
    vote: this.props.model.book.value.yourVote ?? 0,
    votedAlready: (this.props.model.book.value.yourVote ?? 0) !== 0,
    userWordsFetched: false,
  };

  componentDidMount() {
    window.addEventListener("resize", this.fitToViewport);
    this.fetchUserWords();
    this.fetchContinueText();
    analytics.trackEvent("textComplete");
    haptics.play("success");
    this.fitToViewport();
  }

  componentDidUpdate() {
    this.fitToViewport();
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.pendingFit);
    window.removeEventListener("resize", this.fitToViewport);
  }

  fetchUserWords() {
    this.props.remoteUserWords.fetch({
      data: {
        bookID: this.props.model.id,
      },
      success: () => {
        this.setState({ userWordsFetched: true });
      },
    });
  }

  fetchContinueText() {
    readlangClient.request(
      "GET",
      "/api/dashboard/continueText",
      { exceptBookID: this.props.model.id },
      (data) => {
        this.setState({ continueText: data });
      }
    );
  }

  textType(book: BookData) {
    return book.totalWords > 10000
      ? "book"
      : book.category === "Fiction"
        ? "story"
        : book.youTubeID
          ? "video"
          : "text";
  }

  /**
   * Zooms out until it fits the page, but no further than is legible: the
   * page scrolls to what's left
   */
  fitToViewport = () => {
    cancelAnimationFrame(this.pendingFit);
    if (this.props.scrollMode) return;

    this.pendingFit = requestAnimationFrame(() => {
      const endOfText = this.endOfTextRef.current;
      const viewport = document.getElementById("pageTextViewport");
      if (!endOfText || !viewport) return;

      endOfText.style.zoom = "1";

      const viewportBottom = viewport.getBoundingClientRect().bottom;
      let zoom = 1.0;
      while (
        zoom > 0.8 &&
        endOfText.getBoundingClientRect().bottom + viewport.scrollTop >
          viewportBottom
      ) {
        zoom -= 0.05;
        endOfText.style.zoom = `${zoom}`;
      }
    });
  };

  setVote = async (vote: number) => {
    if (
      await signupModal.showIfSignedOut({
        heading: (
          <>
            Sign in to <strong>rate texts</strong>
          </>
        ),
        stores,
      })
    ) {
      return;
    }

    const nextVote = this.state.vote === vote ? 0 : vote;
    this.setState({ vote: nextVote });
    this.props.model.vote(nextVote);
    if (nextVote > 0) {
      void sharePrompt.upvoted();
    }
  };

  renderVoteSection(book: BookData) {
    if (!book.public || this.state.votedAlready) {
      return null;
    }

    return (
      <div class="endOfTextVote">
        <span>Did you like this {this.textType(book)}?</span>
        <button
          class={this.state.vote > 0 ? "selected" : undefined}
          id="upVote"
          title="Yes"
          onClick={() => this.setVote(1)}
        >
          👍
        </button>
        <button
          class={this.state.vote < 0 ? "selected" : undefined}
          id="downVote"
          title="No"
          onClick={() => this.setVote(-1)}
        >
          👎
        </button>
      </div>
    );
  }

  /** What was read and translated, and the streak it adds to */
  renderStats(book: BookData) {
    const characters = languageMap.usesCharacterCount(book.language);
    const translated = this.state.userWordsFetched
      ? this.props.remoteUserWords.totalCount
      : undefined;
    const streakDays = stores.streak.days;
    const stats: [number | string, string][] = [
      [
        characters ? book.metrics.characters : book.metrics.words,
        characters ? "characters read" : "words read",
      ],
      [translated ?? "…", "translated"],
      ...(streakDays
        ? [[`🔥 ${streakDays}`, "day streak"] as [string, string]]
        : []),
    ];
    return (
      <div class="endOfTextStats">
        {stats.map(([value, label]) => (
          <div key={label} class="endOfTextStat">
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
    );
  }

  renderPracticeButton(book: BookData) {
    const count = this.props.remoteUserWords.totalCount;
    if (!this.state.userWordsFetched || count < 1) {
      return null;
    }

    return (
      <a
        class="payButton"
        href={`/${book.language}/flashcards/book/${this.props.model.id}`}
      >
        Practice {count === 1 ? "the word" : `${count} words`} you translated
      </a>
    );
  }

  /** "‹ Back to Library", as the app's back buttons, to the tab it's from */
  renderReturnLink(book: BookData) {
    const [tab, href] = book.starterStory
      ? ["Home", "/"]
      : stores.user.id.value === book.ownerID?.toString()
        ? ["Library", "/myTexts"]
        : ["Discover", "/library"];
    return (
      <Button kind="plain" icon="icon-chevron-left" href={href}>
        Back to {tab}
      </Button>
    );
  }

  render() {
    const book = this.props.model.book.value;
    const { continueText } = this.state;

    return (
      <div dir="ltr" id="endOfText" ref={this.endOfTextRef}>
        <div class="endOfTextSummary">
          {/* A background, so the stylesheet can draw the Dark theme's own */}
          <div class="endOfTextRabbit" />
          <h2 class="endOfTextHeader">Finished!</h2>
          <p class="endOfTextTitle">{utils.elide(book.title, 120)}</p>
          {this.renderStats(book)}
        </div>
        <div class="endOfTextActions">
          {this.renderVoteSection(book)}
          {this.renderPracticeButton(book)}
          {continueText ? (
            <a class="endOfTextNext" href={`/library/${continueText._id}`}>
              <span>Read next</span>
              <strong>{displayTitle(continueText.title ?? "")}</strong>
            </a>
          ) : null}
          <p>{this.renderReturnLink(book)}</p>
        </div>
      </div>
    );
  }
}
